<template>
  <div class="hero-wrapper">
    <div class="container text-center">
      <slot />

      <!--  Card: Desktop  -->
      <div class="card-row hide-in-mobile">
        <div class="card">
            <p class="card-title mr-4">Your total protection score</p>
            <circular-progress :value="protection_score" :size="88" font-color />
        </div>
        <div class="card">
          <div>
            <p class="card-title">Current budget being wasted</p>
            <p class="budget-wasted">{{ budget_wasted }}<small>/m</small></p>
          </div>

          <img src="@/assets/images/icons/money_fire.svg" alt="budget wasted" loading="eager">
        </div>
      </div>

      <div class="date hide-in-mobile">{{ date }}</div>
      <button class="btn-orange hide-in-mobile" @click="handleCta">Improve my score</button>

      <!--   Card: Mobile   -->
      <div class="card hide-in-desktop mx-auto">
          <p class="card-title">Current budget being wasted</p>
          <div style="display: flex; align-items: center">
            <img class="mr-2" src="@/assets/images/icons/money_fire.svg" alt="budget wasted" style="width: 28px" loading="eager">
            <p class="budget-wasted">{{ budget_wasted }}<small>/m</small></p>
          </div>
          <hr style="width: 100%; margin: 16px auto;">
          <p class="card-title">Your total protection score</p>
          <div class="date">{{ date }}</div>
          <circular-progress :value="protection_score" font-color />
          <button class="btn-orange" @click="handleCta" style="margin-top: 24px">Improve my score</button>
      </div>

    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { currencyAndSymbol, navigateToSignup, navigateToPricing, formatScore } from "@/utils";
import CircularProgress from "@/components/global/progress/Circular";

export default {
  name: "Hero",
  props: {
    pricingCta: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CircularProgress,
  },
  setup() {
    return { navigateToSignup, navigateToPricing };
  },
  computed: {
    ...mapGetters(['get_full_name', 'get_domains', 'get_selected_domain', 'get_account']),

    domain_getter() {
      const options = {
        'Report 2 One Domain': this.get_domains[0],
        'Report 2 Multiple Domains': this.get_account,
        'Report 2 Selected': this.get_selected_domain,
      };

      return options[ this.$route.name ];
    },

    single() {
      const domains = this.get_domains || [];
      return Object.keys( domains ).length === 1;
    },

    protection_score() {
      return formatScore(this.domain_getter?.ProtectionScore);
    },

    budget_wasted() {
      const PotentialSavingMultiplier = 0.15;
      const domains = this.get_domains || [];
      const singleDomainRoute = ['Report 2 One Domain', 'Report 2 Selected'].includes( this.$route.name );

      if ( singleDomainRoute && this.domain_getter) {
        const Currency = this.domain_getter.Currency;
        const PotentialSaving = this.domain_getter.PotentialSaving
          ? this.domain_getter.PotentialSaving
          : this.domain_getter.SpendLast30Days * PotentialSavingMultiplier;
        return currencyAndSymbol(PotentialSaving, Currency, true);
      }
      if (this.get_account?.PotentialSaving && this.get_account?.AccountCurrency) {
        const { PotentialSaving, AccountCurrency } = this.get_account
        return currencyAndSymbol(PotentialSaving, AccountCurrency, true);
      }

      const PotentialSaving = domains.reduce( (acc, domain) => {
        return ('SpendLast30Days' in domain) ? acc + domain.SpendLast30Days : acc;
      }, 0) * PotentialSavingMultiplier;
      return currencyAndSymbol(PotentialSaving, this.get_account?.AccountCurrency, true);
    },

    date() {
      let _date = new Date();
      const month = _date.toLocaleString('default', { month: 'short' });
      const day = _date.getUTCDate();
      const year = _date.getFullYear();
      const dayEnd = () => {
        if (day === 1)  return 'st';
        if (day === 2) return 'nd';
        if (day === 3) return 'rd';
        return 'th';
      };

      return `As of ${month}. ${day}${dayEnd()} ${year} | based on the last 30 days`;
    },
  },
  methods: {
    ...mapMutations(['SET_SELECTED_DOMAIN']),

    handleCta() {
      return this.pricingCta ? navigateToPricing() : navigateToSignup();
    },

    backToDomains() {
      this.SET_SELECTED_DOMAIN(null);
      this.$router.push({ name: 'Report 2 Multiple Domains' })
    },
  }
}
</script>

<style lang="scss" scoped>
@import "src/assets/style/responsive";
@import "src/assets/style/colors";

.hero-wrapper {
  display: block;
  min-height: 400px;
  background: transparent linear-gradient(180deg, #FDFBFB 0%, #FBF7F6 100%) 0% 0% no-repeat padding-box;

  @include mobile {
    background: transparent linear-gradient(180deg, #FCFAF9 0%, var(--white) 49%, #FAF4F2 100%) 0% 0% no-repeat padding-box;
  }
}
.container {
  padding-top: 52px;
  padding-bottom: 56px;

  @include mobile {
    padding-bottom: 52px;

  }
}
.card-row {
  display: flex;
  justify-content: center;
  margin-top: 22px;
}
.card {
  display: flex;
  width: 376px;
  max-width: 40vw;
  height: auto;
  align-items: center;

  @include mobile {
    width: 320px;
    max-width: 90vw;
    flex-direction: column;
  }
  @include sm {
    width: 100%;
  }

  &:first-child {
    margin-right: 16px;
  }
}
.card-title {
  font-family: "Sofia Pro Black";
  font-size: 24px;
  text-align: left;
  color: $black;

  @include mobile {
    font-size: 16px;
  }
}
.budget-wasted {
  font-family: "Sofia Pro Black";
  font-size: 28px;
  text-align: left;
  color: $orange;

  @include mobile {
    font-size: 20px;
    margin-bottom: unset;
  }
  small {
    font-family: "Sofia Pro";
    font-size: 20px;

    @include mobile {
      font-size: 16px;
    }
  }
}
.date {
  font-family: "Sofia Pro Light";
  font-size: 16px;
  color: $darkGrayForOrange;
  margin-top: 24px;
  margin-bottom: 44px;

  @include mobile {
    font-size: 12px;
    margin-top: 0;
    margin-bottom: 24px;
  }
}
</style>